import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CutePlayer from '../components/CutePlayer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiImage, FiRadio, FiType, FiCopy, FiCheck, FiArrowRight, FiArrowLeft, FiZap, FiBox } = FiIcons;

const Builder = () => {
  const [step, setStep] = useState(0); 
  const [config, setConfig] = useState({
    logoUrl: "", 
    streamUrl: "",
    stationName: "Console Radio"
  });
  const [copied, setCopied] = useState(false);

  // CRITICAL: Construct the Standalone Player URL
  const standaloneUrl = useMemo(() => {
    const origin = window.location.origin;
    const pathname = window.location.pathname.endsWith('/') 
      ? window.location.pathname 
      : window.location.pathname + '/';
    
    const params = new URLSearchParams({
      stream: config.streamUrl,
      logo: config.logoUrl,
      name: config.stationName
    }).toString();
    
    return `${origin}${pathname}#/render?${params}`;
  }, [config]);

  const embedCode = `<iframe src="${standaloneUrl}" width="100%" height="450px" frameborder="0" allow="autoplay" scrolling="no" style="border:none; border-radius: 40px; overflow:hidden; background:transparent;"></iframe>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  return (
    <div className="min-h-screen bg-[#FDFEFF] flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#00AEEF]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#00AEEF]/5 rounded-full blur-[120px]" />

      <div className="w-full max-w-5xl z-10">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="landing" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="text-center space-y-8">
              <div className="inline-flex items-center gap-3 bg-white border border-gray-100 px-4 py-2 rounded-2xl shadow-sm mb-4">
                <SafeIcon icon={FiZap} className="text-[#00AEEF]" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Builder V2.0</span>
              </div>
              <h1 className="text-6xl md:text-8xl font-black text-gray-900 tracking-tighter leading-none uppercase">Build your <br/> <span className="text-[#00AEEF]">pro player.</span></h1>
              <p className="text-gray-400 font-medium text-lg max-w-xl mx-auto">Create a high-end, gorgeous audio player for your stream in seconds.</p>
              <button onClick={nextStep} className="group flex items-center gap-4 bg-gray-900 text-white px-10 py-6 rounded-[2rem] text-sm font-black uppercase tracking-widest hover:bg-[#00AEEF] transition-all shadow-2xl hover:scale-105 active:scale-95 mx-auto">
                Start Building <SafeIcon icon={FiArrowRight} className="text-xl group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          )}

          {/* Steps 1-3 remain same... skipping for brevity but they are in the full file */}
          {(step > 0 && step < 4) && (
             <div className="bg-white p-12 rounded-[3.5rem] shadow-2xl border border-gray-50 flex flex-col items-center">
                {step === 1 && (
                  <>
                    <h2 className="text-4xl font-black text-gray-900 uppercase tracking-tight mb-8">Station Logo</h2>
                    <input type="text" value={config.logoUrl} onChange={(e) => setConfig({...config, logoUrl: e.target.value})} placeholder="Logo URL..." className="w-full max-w-md bg-gray-50 border border-gray-100 rounded-2xl px-6 py-5 text-center text-sm font-bold" />
                  </>
                )}
                {step === 2 && (
                  <>
                    <h2 className="text-4xl font-black text-gray-900 uppercase tracking-tight mb-8">Stream URL</h2>
                    <input type="text" value={config.streamUrl} onChange={(e) => setConfig({...config, streamUrl: e.target.value})} placeholder="Stream MP3 URL..." className="w-full max-w-md bg-gray-50 border border-gray-100 rounded-2xl px-6 py-5 text-center text-sm font-bold" />
                  </>
                )}
                {step === 3 && (
                  <>
                    <h2 className="text-4xl font-black text-gray-900 uppercase tracking-tight mb-8">Station Name</h2>
                    <input type="text" value={config.stationName} onChange={(e) => setConfig({...config, stationName: e.target.value})} placeholder="Station Name..." className="w-full max-w-md bg-gray-50 border border-gray-100 rounded-2xl px-6 py-5 text-center text-sm font-bold" />
                  </>
                )}
                <div className="flex gap-4 mt-8">
                  <button onClick={prevStep} className="p-4 bg-gray-50 text-gray-400 rounded-2xl hover:bg-gray-100 transition-all"><SafeIcon icon={FiArrowLeft} /></button>
                  <button onClick={nextStep} className="flex items-center gap-3 bg-[#00AEEF] text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg">Next <SafeIcon icon={FiArrowRight} /></button>
                </div>
             </div>
          )}

          {step === 4 && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="space-y-12">
              <div className="text-center">
                <h2 className="text-4xl font-black text-gray-900 uppercase tracking-tight">Player Ready</h2>
                <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mt-2">Live Preview & Sharing Options</p>
              </div>

              <div className="w-full">
                <CutePlayer 
                  streamUrl={config.streamUrl} 
                  logoUrl={config.logoUrl} 
                  stationName={config.stationName}
                  shareUrl={standaloneUrl} 
                />
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Embed Code */}
                <div className="bg-gray-50 border border-gray-100 rounded-[3rem] p-10">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Iframe Embed</span>
                    <button onClick={handleCopy} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl text-[10px] font-black text-gray-900 border border-gray-200 hover:bg-gray-900 hover:text-white transition-all">
                      <SafeIcon icon={copied ? FiCheck : FiCopy} /> {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-gray-900 rounded-3xl p-6 font-mono text-[11px] text-cyan-400 break-all h-24 overflow-y-auto">{embedCode}</div>
                </div>

                {/* Direct Link */}
                <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-[3rem] p-10">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] font-black text-[#00AEEF] uppercase tracking-[0.3em]">Direct Player Link</span>
                    <button onClick={() => {navigator.clipboard.writeText(standaloneUrl); setCopied(true); setTimeout(()=>setCopied(false),2000)}} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl text-[10px] font-black text-[#00AEEF] border border-[#BAE6FD] hover:bg-[#00AEEF] hover:text-white transition-all">
                      <SafeIcon icon={copied ? FiCheck : FiCopy} /> Copy Link
                    </button>
                  </div>
                  <div className="text-xs font-bold text-gray-600 line-clamp-3 leading-relaxed">{standaloneUrl}</div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button onClick={() => setStep(0)} className="px-6 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-900 transition-colors">Create New Player</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Builder;